import * as React from 'react';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import type {
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  FunnelSeriesOption,
} from 'echarts';

import type { HeroPaymentCycleFunnelTotals } from './heroPaymentCycleFunnelApi';

type ECOption = echarts.ComposeOption<
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | FunnelSeriesOption
>;

echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  FunnelChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

const HeroPaymentCycleCampaignFunnelChart: React.FC<{ totals: HeroPaymentCycleFunnelTotals }> = ({
  totals,
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    const data = [
      { name: 'WhatsApp sent', value: totals.whatsapp_sent || 0 },
      { name: 'Opened quote link', value: totals.link_opened || 0 },
      { name: 'Chose payment term', value: totals.payment_term_selected || 0 },
      { name: 'Saved on profile', value: totals.preferred_term_on_profile || 0 },
    ];

    if (!chartRef.current) return undefined;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const option: ECOption = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} buyers',
      },
      series: [
        {
          name: 'Hero credit cycle',
          type: 'funnel',
          left: '10%',
          width: '80%',
          min: 0,
          max: Math.max(data[0]?.value || 1, 1),
          minSize: '0%',
          maxSize: '100%',
          sort: 'descending',
          gap: 4,
          label: {
            show: true,
            position: 'inside',
            formatter: '{b}\n{c}',
            fontSize: 12,
          },
          data,
        },
      ],
    };

    chartInstance.current.setOption(option);

    const onResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [totals]);

  return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default HeroPaymentCycleCampaignFunnelChart;
